import React, {useState} from "react";
import {Navigate, Link as RouterLink} from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {Modal} from "react-bootstrap";
import Privacy from "../privacy";
import {CheckCircleOutline, Person} from "@mui/icons-material";
import {useUserAuth} from "../../context/UserAuthContext";
import {COLLEGES, friendlyErrorMessage, nowFormatted} from "../../services/associationService";
import {
    ACADEMIC_TITLES,
    DEFAULT_ENGINEER_PHOTO,
    GENDERS,
    addEngineerRecord,
    uploadEngineerPhoto,
    validateEngineerRegistration,
} from "../../services/engineerService";

const PRIMARY = "#3E4F88";

/**
 * Registo de engenheiro no browser (Fase 2).
 *
 * Fluxo igual à app ESFAPP:
 *   1. Criar conta Firebase (email + password)
 *   2. Enviar email de verificação
 *   3. Carregar a foto para User/{uid}.jpg (fallback: foto por omissão)
 *   4. Criar o registo em "User" com Enabled=false (um admin ativa depois)
 *
 * Campos obrigatórios iguais à app: foto, email, password, nomes, género,
 * nº OE (membro OE) ou instituição + nº estudante (estudante),
 * título académico, colégio da engenharia e termos.
 */
const EngineerRegisterPage = () => {

    const {user, role, signUp, verifyEmail} = useUserAuth();

    // Campos
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordAgain, setPasswordAgain] = useState("");
    const [gender, setGender] = useState("");
    const [profileType, setProfileType] = useState("Membro OE");
    const [oeNumber, setOeNumber] = useState("");
    const [university, setUniversity] = useState("");
    const [studentNumber, setStudentNumber] = useState("");
    const [academicTitle, setAcademicTitle] = useState("");
    const [college, setCollege] = useState("");
    const [company, setCompany] = useState("");
    const [address, setAddress] = useState("");
    const [subcollege, setSubcollege] = useState("");
    const [phone, setPhone] = useState("");
    const [linkedin, setLinkedin] = useState("");
    const [info, setInfo] = useState("");
    const [terms, setTerms] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [done, setDone] = useState(false);
    // false = o registo correu mas o email de verificação não foi enviado
    const [emailSent, setEmailSent] = useState(true);
    // true = a foto não conseguiu ser carregada (usámos a foto por omissão)
    const [photoFallback, setPhotoFallback] = useState(false);
    const [showPolicy, setShowPolicy] = useState(false);

    // Já autenticado e sem registo em curso? Não precisa de se registar.
    // (O flag "registering" evita o redirecionamento prematuro logo após
    //  o signUp, enquanto a foto e o registo RTDB ainda estão a ser criados.)
    if (user && !done && !registering) {
        if (role === "engineer") {
            return <Navigate to="/app/engenheiro" replace/>;
        }
        return <Navigate to="/login" replace/>;
    }

    const onPhotoChange = (e) => {
        const file = e.target.files && e.target.files[0];
        setPhoto(file || null);
        if (file) {
            setPhotoPreview(URL.createObjectURL(file));
        } else {
            setPhotoPreview(null);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");

        const fields = {
            photo,
            email,
            password,
            passwordAgain,
            firstName,
            lastName,
            gender,
            profileType,
            oeNumber,
            university,
            studentNumber,
            academicTitle,
            college,
            terms,
        };
        const problem = validateEngineerRegistration(fields);
        if (problem) {
            setError(problem);
            return;
        }

        setLoading(true);
        setRegistering(true);
        try {
            // 1) Criar conta Firebase (erros específicos tratados no catch final)
            const cred = await signUp(email.trim(), password);

            // 2) Email de verificação (igual à app).
            //    Se falhar, o registo continua: o email pode ser reenviado a
            //    partir da vista de login ("Reenviar email de verificação").
            let emailOk = true;
            try {
                await verifyEmail(cred.user);
            } catch (err) {
                console.error("Falha ao enviar o email de verificação (o registo continua):", err);
                emailOk = false;
            }

            // 3) Carregar a foto (mesmo caminho da app: User/{uid}.jpg).
            //    Se o upload falhar (ex.: storage do Firebase indisponível), usamos a
            //    foto por omissão para não bloquear o registo — igual às associações.
            let profileUrl;
            let fallback = false;
            try {
                profileUrl = await uploadEngineerPhoto(cred.user.uid, photo);
            } catch (err) {
                console.error("Falha no upload da foto; a usar a foto por omissão.", err);
                profileUrl = DEFAULT_ENGINEER_PHOTO;
                fallback = true;
            }

            // 4) Registo "User" (Enabled=false até um admin ativar) —
            //    mesmos campos e valores que a app escreve (incluindo "" nos
            //    campos do tipo de perfil não escolhido).
            const record = {
                Id: cred.user.uid,
                Email: email.trim(),
                ProfileURL: profileUrl,
                FirstName: firstName.trim(),
                LastName: lastName.trim(),
                Gender: gender,
                Company: company.trim(),
                Address: address.trim(),
                AcademicTitles: academicTitle,
                Course: college,
                Subcollege: subcollege.trim(),
                PhoneNumber: phone.trim(),
                OENumber: profileType === "Membro OE" ? oeNumber.trim() : "",
                OtherInfo: info.trim(),
                Linkedin: linkedin.trim(),
                Dicas: "",
                Enabled: false,
                EmailVerified: false,
                RegisterDate: nowFormatted(),
                Points: 0,
                Usertoken: "", // no browser não há OneSignal
                ProfileType: profileType,
                StudentNumber: profileType === "Estudante" ? studentNumber.trim() : "",
                University: profileType === "Estudante" ? university.trim() : "",
            };
            await addEngineerRecord(record);

            setEmailSent(emailOk);
            setPhotoFallback(fallback);
            setDone(true);
        } catch (err) {
            const specific = friendlyErrorMessage(err);
            // A mensagem (err.message) tem a descrição real; o code vem depois.
            const detail = (err && (err.message || err.code || err.name)) || "desconhecido";
            console.error("Erro no registo:", err);
            if (specific) {
                setError(specific);
            } else {
                setError(
                    `O registo não foi concluído (erro: ${detail}). ` +
                    "Se a conta de email foi criada, confirma o email e tenta terminar o registo novamente, " +
                    "ou contacta esf@centro.oep.pt."
                );
            }
        } finally {
            setLoading(false);
            // Nota: em caso de falha o "registering" mantém-se true de propósito,
            // para não redirecionar o utilizador antes de ver a mensagem de erro.
        }
    };

    // ---------- vista de sucesso ----------
    if (done) {
        return (
            <Container maxWidth="sm" sx={{py: 8}}>
                <Stack spacing={3} alignItems="center" sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 5}}>
                    <CheckCircleOutline sx={{fontSize: 56, color: "#2e7d32"}}/>
                    <Typography variant="h5" sx={{fontWeight: 700}}>Registo efetuado com sucesso</Typography>
                    <Alert severity="success" sx={{alignItems: "flex-start"}}>
                        {emailSent ? (
                            <>
                                Confirma o teu email: chegou-te um email de verificação para <b>{email.trim()}</b>.
                                Depois de verificar o email, um administrador da plataforma irá ativar a tua conta.
                            </>
                        ) : (
                            <>
                                O registo foi criado, mas não foi possível enviar o email de verificação de momento.
                                Entra na plataforma ("Ir para o login") e usa "Reenviar email de verificação"
                                quando tiveres oportunidade. Depois de verificar o email, um administrador
                                da plataforma irá ativar a tua conta.
                            </>
                        )}
                    </Alert>
                    {photoFallback && (
                        <Alert severity="warning" sx={{alignItems: "flex-start"}}>
                            Não foi possível carregar a tua foto de momento, pelo que a conta ficou
                            com a foto por omissão. O registo não ficou afetado.
                        </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
                        Não recebeste o email? Verifica a pasta de spam.
                        Em caso de dúvidas, contacta <b>esf@centro.oep.pt</b>.
                    </Typography>
                    <Button
                        component={RouterLink}
                        to="/login"
                        variant="contained"
                        size="large"
                        sx={{bgcolor: PRIMARY, fontWeight: 600}}
                    >
                        Ir para o login
                    </Button>
                </Stack>
            </Container>
        );
    }

    // ---------- formulário ----------
    return (
        <Box component="main" sx={{bgcolor: "#f4f5fa", py: 4}}>
            <Container maxWidth="md">
                <Stack spacing={1} sx={{mb: 3}}>
                    <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY}}>
                        Registo de voluntário/a: Engenharia
                    </Typography>
                    <Typography color="text.secondary">
                        Cria o teu perfil para conhecer desafios de organizações sem fins lucrativos
                        e colaborar em projetos com impacto social.
                        Os campos assinalados com * são obrigatórios.
                    </Typography>
                </Stack>

                <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 4}}>
                    <Button
                        size="small"
                        component={RouterLink}
                        to="/"
                        sx={{textTransform: "none", alignSelf: "flex-start", color: "text.secondary", mb: 1}}
                    >
                        ← Voltar à página inicial
                    </Button>
                    <form onSubmit={submit} noValidate>
                        <Stack spacing={2}>
                            {error && <Alert severity="error">{error}</Alert>}

                            {/* Foto de perfil */}
                            <Stack direction={{xs: "column", sm: "row"}} spacing={3} alignItems="center">
                                <Avatar
                                    src={photoPreview || undefined}
                                    sx={{width: 72, height: 72, border: 2, borderColor: photo ? "success.main" : "error.main"}}
                                >
                                    <Person fontSize="large"/>
                                </Avatar>
                                <Stack spacing={1} sx={{flex: 1, alignItems: "stretch"}}>
                                    <Typography variant="body2" fontWeight={600}>
                                        Foto de perfil *
                                    </Typography>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        sx={{color: PRIMARY, borderColor: PRIMARY, alignSelf: "flex-start"}}
                                    >
                                        Escolher imagem
                                        <input type="file" accept="image/*" hidden onChange={onPhotoChange}/>
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                        JPG/PNG. Será a tua foto de perfil na plataforma.
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Primeiro nome"
                                    size="small"
                                    required
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    sx={{flex: 1}}
                                />
                                <TextField
                                    label="Último nome"
                                    size="small"
                                    required
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    sx={{flex: 1}}
                                />
                            </Stack>

                            <TextField
                                label="Email"
                                type="email"
                                size="small"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Palavra-passe"
                                    type="password"
                                    size="small"
                                    required
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    sx={{flex: 1}}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography variant="caption" color="text.secondary" component="span">
                                                    mín. 6 caracteres
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label="Confirmar palavra-passe"
                                    type="password"
                                    size="small"
                                    required
                                    autoComplete="new-password"
                                    value={passwordAgain}
                                    onChange={(e) => setPasswordAgain(e.target.value)}
                                    sx={{flex: 1}}
                                />
                            </Stack>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Género"
                                    size="small"
                                    select
                                    required
                                    value={gender}
                                    onChange={(e) => setGender(e.target.value)}
                                    sx={{flex: 1}}
                                >
                                    {GENDERS.map((g) => (
                                        <MenuItem key={g} value={g}>{g}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    label="Título académico"
                                    size="small"
                                    select
                                    required
                                    value={academicTitle}
                                    onChange={(e) => setAcademicTitle(e.target.value)}
                                    sx={{flex: 1}}
                                >
                                    {ACADEMIC_TITLES.map((t) => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </TextField>
                            </Stack>

                            <TextField
                                label="Colégio da engenharia"
                                size="small"
                                select
                                required
                                value={college}
                                onChange={(e) => setCollege(e.target.value)}
                            >
                                {COLLEGES.map((c) => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </TextField>

                            {/* Tipo de perfil (igual aos radio buttons da app) */}
                            <Box>
                                <Typography variant="body2" fontWeight={600} sx={{mb: 1}}>
                                    Tipo de perfil *
                                </Typography>
                                <RadioGroup
                                    row
                                    value={profileType}
                                    onChange={(e) => setProfileType(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="Membro OE"
                                        control={<Radio sx={{color: PRIMARY, "&.Mui-checked": {color: PRIMARY}}}/>}
                                        label="Membro da Ordem dos Engenheiros"
                                    />
                                    <FormControlLabel
                                        value="Estudante"
                                        control={<Radio sx={{color: PRIMARY, "&.Mui-checked": {color: PRIMARY}}}/>}
                                        label="Estudante"
                                    />
                                </RadioGroup>

                                {profileType === "Membro OE" ? (
                                    <TextField
                                        label="Número OE"
                                        size="small"
                                        required
                                        value={oeNumber}
                                        onChange={(e) => setOeNumber(e.target.value)}
                                        sx={{mt: 1}}
                                    />
                                ) : (
                                    <Stack direction={{xs: "column", sm: "row"}} spacing={2} sx={{mt: 1}}>
                                        <TextField
                                            label="Instituição de ensino"
                                            size="small"
                                            required
                                            value={university}
                                            onChange={(e) => setUniversity(e.target.value)}
                                            sx={{flex: 2}}
                                        />
                                        <TextField
                                            label="Número de estudante"
                                            size="small"
                                            required
                                            value={studentNumber}
                                            onChange={(e) => setStudentNumber(e.target.value)}
                                            sx={{flex: 1}}
                                        />
                                    </Stack>
                                )}
                            </Box>

                            <Typography variant="body2" color="text.secondary">
                                Informação adicional (opcional)
                            </Typography>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Empresa"
                                    size="small"
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                    sx={{flex: 2}}
                                />
                                <TextField
                                    label="Telefone"
                                    size="small"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    sx={{flex: 1}}
                                />
                            </Stack>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Morada"
                                    size="small"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    sx={{flex: 2}}
                                />
                                <TextField
                                    label="Área de trabalho"
                                    size="small"
                                    value={subcollege}
                                    onChange={(e) => setSubcollege(e.target.value)}
                                    sx={{flex: 1}}
                                />
                            </Stack>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="LinkedIn"
                                    size="small"
                                    value={linkedin}
                                    onChange={(e) => setLinkedin(e.target.value)}
                                    sx={{flex: 1}}
                                />
                                <TextField
                                    label="Competências e áreas de interesse"
                                    size="small"
                                    multiline
                                    minRows={2}
                                    value={info}
                                    onChange={(e) => setInfo(e.target.value)}
                                    helperText="Indica os tipos de projetos em que gostarias de colaborar (opcional)"
                                    sx={{flex: 2}}
                                />
                            </Stack>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={terms}
                                        onChange={(e) => setTerms(e.target.checked)}
                                        sx={{color: PRIMARY, "&.Mui-checked": {color: PRIMARY}}}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{display: "inline"}}>
                                        Aceito os termos de utilização e a{" "}
                                        <Button
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPolicy(true);
                                            }}
                                            sx={{
                                                textTransform: "none",
                                                color: PRIMARY,
                                                fontWeight: 600,
                                                p: 0,
                                                minWidth: "auto",
                                                lineHeight: 1.6,
                                                textDecoration: "underline",
                                            }}
                                        >
                                            política de privacidade
                                        </Button>{" "}
                                        da plataforma. *
                                    </Typography>
                                }
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                                sx={{bgcolor: PRIMARY, fontWeight: 600, alignSelf: "flex-start"}}
                            >
                                {loading ? "A registar…" : "Registar"}
                            </Button>
                        </Stack>
                    </form>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
                    Já tens conta?{" "}
                    <RouterLink to="/login" style={{color: PRIMARY, fontWeight: 600}}>
                        Inicia sessão
                    </RouterLink>
                    {" · "}
                    <RouterLink to="/registo/associacao" style={{color: PRIMARY, fontWeight: 600}}>
                        Registo de associação
                    </RouterLink>
                </Typography>
            </Container>

            {/* Política de privacidade em modal (mesmo conteúdo da página /privacy) —
                o formulário mantém os dados preenchidos ao fechar */}
            <Modal show={showPolicy} onHide={() => setShowPolicy(false)} size="lg">
                <Modal.Header closeButton/>
                <Modal.Body>
                    <Privacy/>
                </Modal.Body>
            </Modal>
        </Box>
    );
};

export default EngineerRegisterPage;
